import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { Popover, PopoverModule } from 'primeng/popover';
import { isMenuUrlActive, menuRouterUrl } from '../app-sidebar-nav/app-sidebar-nav.component';
import { isMenuCategory } from '../../_services/menu-organization';

/** Blocco della barra: le voci dirette del gruppo (senza nome) o un sottogruppo (nome sotto, come i gruppi di Word). */
interface BloccoRibbon {
	titolo: string;
	icona: string;
	voci: any[];
}

/**
 * Sottogruppo della seconda barra mostrato come una voce con la freccia: al passaggio del mouse (o al clic, per il
 * touch) apre sotto la tendina con le sue voci, come i gruppi del menu in alto con lo stile a tendina.
 */
@Component({
	selector: 'app-ribbon-tendina',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslatePipe, PopoverModule],
	template: `
		<a class="kcs-ribbon-voce" role="menuitem" tabindex="0" aria-haspopup="true" [class.active]="attivo()" (mouseenter)="entra($event)" (mouseleave)="esci()" (click)="op.show($event)" (keydown.enter)="op.show($event)">
			@if (blocco.icona) {
				<i [class]="blocco.icona" aria-hidden="true"></i>
			}
			<span>{{ blocco.titolo | translate }}</span>
			<i class="pi pi-angle-down kcs-ribbon-caret" aria-hidden="true"></i>
		</a>
		<p-popover #op appendTo="body" (onShow)="posiziona()" styleClass="menu-popover topnav-popover">
			<div (click)="op.hide()">
				<ul class="nav">
					@for (v of blocco.voci; track $index) {
						<li class="nav-item w-full">
							<a class="pl-1 nav-link nav-dropdown-toggle" [class.active]="isActive(v)" (click)="vai.emit(v)">
								@if (v.styleclass) {
									<i [class]="v.styleclass"></i>
								}
								<span>{{ v.pg_title | translate }}</span>
							</a>
						</li>
					}
				</ul>
			</div>
		</p-popover>
	`,
})
export class RibbonTendinaComponent {
	@Input() blocco!: BloccoRibbon;
	@Input() isActive: (v: any) => boolean = () => false;
	@Output() vai = new EventEmitter<any>();
	@ViewChild('op') op!: Popover;
	private chiusura: any;

	attivo(): boolean {
		return this.blocco.voci.some((v) => this.isActive(v));
	}

	entra(e: Event) {
		clearTimeout(this.chiusura);
		this.op.show(e);
	}

	esci() {
		clearTimeout(this.chiusura);
		this.chiusura = setTimeout(() => this.op.hide(), 200);
	}

	/** Sotto la voce, come le tendine del menu in alto; l'hover sulla tendina la tiene aperta. */
	posiziona() {
		const rect = (this.op.target as HTMLElement).getBoundingClientRect();
		const box = this.op.container as HTMLElement;
		box.style.left = `${rect.left}px`;
		box.style.top = `${rect.bottom + 6}px`;
		box.onmouseenter = () => clearTimeout(this.chiusura);
		box.onmouseleave = () => this.esci();
	}
}

/**
 * Menu in alto con stile "barra come Word" (cmp_menu_org.stileSottomenu = 'barra'): il clic su un
 * gruppo della barra apre sotto l'header una seconda barra fissa con le sue voci, che resta aperta.
 * Le voci stanno su una riga; i sottogruppi sono voci con la tendina in hover, oppure (cmp_menu_org.barraSottogruppi =
 * 'blocchi') blocchi affiancati col nome sotto.
 * Alta quanto il contenuto: mentre e' aperta
 * html[kcs-ribbon] e --kcs-ribbon-height (misurata) fanno scendere la pagina della sua altezza.
 */
@Component({
	selector: 'app-menu-ribbon',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	// le regole su html[kcs-ribbon] spostano il corpo della pagina: devono essere globali
	encapsulation: ViewEncapsulation.None,
	imports: [TranslatePipe, TooltipModule, RibbonTendinaComponent],
	template: `
		<div #barra class="kcs-ribbon" role="menu" [class.kcs-ribbon-a-capo]="aCapo" [attr.aria-label]="gruppo?.pg_title | translate">
			@for (b of blocchi; track $index; let ultimo = $last) {
				@if (!stileBlocchi && b.titolo) {
					<app-ribbon-tendina class="kcs-ribbon-blocco" [blocco]="b" [isActive]="attiva" (vai)="vai($event)"></app-ribbon-tendina>
				} @else {
				<div class="kcs-ribbon-blocco">
					<div class="kcs-ribbon-voci">
						@for (v of b.voci; track $index) {
							<a class="kcs-ribbon-voce" role="menuitem" tabindex="0" [class.active]="isActive(v)" (click)="vai(v)" (keydown.enter)="vai(v)">
								@if (v.styleclass) {
									<i [class]="v.styleclass" aria-hidden="true"></i>
								}
								<span>{{ v.pg_title | translate }}</span>
							</a>
						}
					</div>
					@if (b.titolo) {
						<div class="kcs-ribbon-titolo">{{ b.titolo | translate }}</div>
					}
				</div>
				}
				<!-- a tendina il separatore resta solo dopo le voci dirette del gruppo, non tra un sottogruppo e l'altro -->
				@if (!ultimo && (stileBlocchi || !b.titolo)) {
					<span class="kcs-ribbon-sep" aria-hidden="true"></span>
				}
			}
			<button type="button" class="kcs-ribbon-chiudi" (click)="chiudi.emit()" [pTooltip]="'Chiudi la barra' | translate" tooltipPosition="left" [attr.aria-label]="'Chiudi la barra' | translate"><i class="pi pi-angle-up"></i></button>
		</div>
	`,
	styleUrl: './app-menu-ribbon.component.css',
})
export class AppMenuRibbonComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	/** Gruppo della barra in alto (voce senza pagina, con sottomenu) */
	@Input() gruppo: any;
	/** Voci che non entrano: a capo (la barra si alza) invece dello scorrimento orizzontale (cmp_menu_org.barraACapo) */
	@Input() aCapo = false;
	/** Sottogruppi come blocchi col nome sotto (cmp_menu_org.barraSottogruppi = 'blocchi') invece della tendina in hover */
	@Input('blocchi') stileBlocchi = false;
	@Output() chiudi = new EventEmitter<void>();

	@ViewChild('barra') barra?: ElementRef<HTMLElement>;

	blocchi: BloccoRibbon[] = [];
	private ro?: ResizeObserver;

	constructor(
		private router: Router,
		private zone: NgZone,
	) {}

	ngOnInit() {
		document.documentElement.setAttribute('kcs-ribbon', 'on');
	}

	ngAfterViewInit() {
		const el = this.barra?.nativeElement;
		if (!el) return;
		this.zone.runOutsideAngular(() => {
			this.ro = new ResizeObserver(() => document.documentElement.style.setProperty('--kcs-ribbon-height', el.offsetHeight + 'px'));
			this.ro.observe(el);
			// a scorrimento: la rotellina del mouse scorre la barra in orizzontale
			el.addEventListener('wheel', this.rotellina, { passive: false });
		});
	}

	ngOnDestroy() {
		this.ro?.disconnect();
		this.barra?.nativeElement.removeEventListener('wheel', this.rotellina);
		document.documentElement.removeAttribute('kcs-ribbon');
		document.documentElement.style.removeProperty('--kcs-ribbon-height');
	}

	ngOnChanges() {
		const blocchi: BloccoRibbon[] = [];
		let sciolte: BloccoRibbon | null = null;
		for (const voce of this.gruppo?.submenu || []) {
			if (isMenuCategory(voce) && voce.submenu?.length) {
				sciolte = null;
				blocchi.push({ titolo: voce.pg_title, icona: voce.styleclass, voci: this.foglie(voce.submenu) });
			} else if (!isMenuCategory(voce)) {
				if (!sciolte) {
					sciolte = { titolo: '', icona: '', voci: [] };
					blocchi.push(sciolte);
				}
				sciolte.voci.push(voce);
			}
		}
		this.blocchi = blocchi;
	}

	private rotellina = (e: WheelEvent) => {
		const el = e.currentTarget as HTMLElement;
		if (this.aCapo || e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY) || el.scrollWidth <= el.clientWidth) return;
		e.preventDefault();
		el.scrollLeft += e.deltaY;
	};

	/** Voci navigabili di un sottogruppo (i livelli piu' profondi, rari, si appiattiscono). */
	private foglie(voci: any[]): any[] {
		return voci.flatMap((v) => (isMenuCategory(v) ? this.foglie(v.submenu || []) : [v]));
	}

	readonly attiva = (v: any) => this.isActive(v);

	/**
	 * Voce della pagina corrente. Con indirizzi uno dentro l'altro (pagina Fatture "/fatture" e Movimenti bancari
	 * "fatture/movimenti-bancari/16") si accende solo quella con l'indirizzo piu' lungo che corrisponde, come nelle
	 * tendine del menu in alto: prima su Movimenti bancari si accendeva anche il sottogruppo Fatture.
	 */
	isActive(v: any): boolean {
		if ((v?.escludiUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) return false;
		if ((v?.hubUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) return true;
		return isMenuUrlActive(this.router, v?.url) && this.lunghezza(v.url) >= this.corrispondenzaMigliore();
	}

	private lunghezza(url: string): number {
		return ('/' + String(url).split('?')[0].split('#')[0].replace(/^\/+/, '').replace(/\/+$/, '')).length;
	}

	/** Lunghezza dell'indirizzo piu' specifico, tra le voci della barra, che corrisponde alla pagina corrente. */
	private corrispondenzaMigliore(): number {
		let migliore = 0;
		for (const b of this.blocchi) {
			for (const v of b.voci) {
				if (isMenuUrlActive(this.router, v?.url)) migliore = Math.max(migliore, this.lunghezza(v.url));
			}
		}
		return migliore;
	}

	vai(v: any) {
		const url = v?.url;
		if (url == null || url === '' || url === 'null') return;
		/*KCS MI - pulizia variabili navigazione*/
		sessionStorage.clear();
		this.router.navigateByUrl(menuRouterUrl(url));
	}
}
